/**
 * IndexedDB 기반 문제 데이터 캐싱 (Dexie + Web Crypto API 암호화)
 *
 * 흐름: IndexedDB 확인 → 없으면 서버 조회 → 암호화하여 저장
 */

import Dexie, { type EntityTable } from "dexie";
import type { ProblemData } from "@/components/ProblemDataContext";
import { withTimeout } from "@/lib/with-timeout";

// 캐시 설정
const PROBLEM_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7일
const CACHE_WRITE_TIMEOUT_MS = 5000;
const QUOTA_EVICTION_RATIO = 0.1;
const ENCRYPTION_KEY_NAME = "jcg-problem-encryption-key";
const PROBLEM_CACHE_FORMAT_VERSION = 5;
const PROBLEM_CACHE_FORMAT_VERSION_KEY = "problemCacheFormatVersion";

// 암호화된 문제 데이터 타입
interface EncryptedProblem {
  id: string;
  encryptedData: string; // base64 인코딩된 암호화 데이터
  iv: string; // base64 인코딩된 IV
  cachedAt: number;
}

// 메타데이터 레코드 타입
interface MetaRecord {
  key: string;
  value: number;
}

// Dexie 데이터베이스 클래스
class ProblemDatabase extends Dexie {
  problems!: EntityTable<EncryptedProblem, "id">;
  meta!: EntityTable<MetaRecord, "key">;

  constructor() {
    super("jcg-problems");
    this.version(1).stores({
      problems: "id, cachedAt",
    });
    this.version(2).stores({
      problems: "id, cachedAt",
      meta: "key",
    });
  }
}

// 싱글톤 DB 인스턴스
let db: ProblemDatabase | null = null;

// 인메모리 암호화 키 캐시
let cachedEncryptionKey: CryptoKey | null = null;

// 동시에 실패한 저장 작업들이 캐시를 반복 정리하지 않도록 순서대로 처리한다.
let cacheWriteQueue: Promise<void> = Promise.resolve();
let cacheWritesDisabled = false;

function getDb(): ProblemDatabase {
  if (typeof window === "undefined") {
    throw new Error("IndexedDB is not available on server");
  }
  if (!db) {
    db = new ProblemDatabase();
  }
  return db;
}

// ============ 암호화 관련 유틸리티 ============

/**
 * 암호화 키 생성 또는 가져오기
 * localStorage에 저장하여 IndexedDB 데이터와 생명주기 일치
 */
async function getEncryptionKey(): Promise<CryptoKey> {
  if (typeof window === "undefined") {
    throw new Error("Crypto API is not available on server");
  }

  // 인메모리 캐시 확인
  if (cachedEncryptionKey) {
    return cachedEncryptionKey;
  }

  // localStorage에서 키 확인
  const storedKey = localStorage.getItem(ENCRYPTION_KEY_NAME);

  if (storedKey) {
    // 저장된 키 복원
    const keyData = JSON.parse(storedKey);
    cachedEncryptionKey = await crypto.subtle.importKey(
      "jwk",
      keyData,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
    return cachedEncryptionKey;
  }

  // 새 키 생성
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );

  // 키를 localStorage에 저장
  const exportedKey = await crypto.subtle.exportKey("jwk", key);
  localStorage.setItem(ENCRYPTION_KEY_NAME, JSON.stringify(exportedKey));
  cachedEncryptionKey = key;

  return key;
}

/**
 * 데이터 암호화
 */
async function encrypt(
  data: string
): Promise<{ encryptedData: string; iv: string }> {
  const key = await getEncryptionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(data);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded
  );

  return {
    encryptedData: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
    iv: btoa(String.fromCharCode(...iv)),
  };
}

/**
 * 데이터 복호화
 */
async function decrypt(encryptedData: string, iv: string): Promise<string> {
  const key = await getEncryptionKey();

  const encryptedBytes = Uint8Array.from(atob(encryptedData), (c) =>
    c.charCodeAt(0)
  );
  const ivBytes = Uint8Array.from(atob(iv), (c) => c.charCodeAt(0));

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivBytes },
    key,
    encryptedBytes
  );

  return new TextDecoder().decode(decrypted);
}

// ============ 문제 데이터 CRUD ============

/**
 * 문제 데이터 캐시 포맷 버전 확인.
 * v2: solution도 서버에서 evaluateProblemTemplate 처리된 문자열로 저장한다.
 * v3: 문제별 캐시 무효화 메타 누락 이슈 대응으로 전체 문제 캐시를 1회 초기화한다.
 * v4: Firebase 조회에서 누락됐던 caseSensitive 필드를 기존 캐시에 반영한다.
 * v5: 문제별 allowQuotedSqlAlias 필드를 기존 캐시에 반영한다.
 */
export async function ensureProblemCacheFormatVersion(): Promise<boolean> {
  if (typeof window === "undefined") return false;

  try {
    const database = getDb();
    const record = await database.meta.get(PROBLEM_CACHE_FORMAT_VERSION_KEY);
    if (record?.value === PROBLEM_CACHE_FORMAT_VERSION) {
      return false;
    }

    await database.problems.clear();
    localStorage.removeItem(ENCRYPTION_KEY_NAME);
    cachedEncryptionKey = null;
    await database.meta.put({
      key: PROBLEM_CACHE_FORMAT_VERSION_KEY,
      value: PROBLEM_CACHE_FORMAT_VERSION,
    });
    console.log("[problemDb] 문제 캐시 포맷 변경으로 캐시를 초기화했습니다.");
    return true;
  } catch (error) {
    console.error("[problemDb] 캐시 포맷 버전 확인 실패:", error);
    return false;
  }
}

/**
 * IndexedDB에서 문제 데이터 조회 (다중)
 * @returns { hits: 캐시된 데이터, misses: 캐시 미스 ID들 }
 */
export async function getProblemsFromDb(ids: string[]): Promise<{
  hits: Record<string, ProblemData>;
  misses: string[];
}> {
  if (typeof window === "undefined") {
    return { hits: {}, misses: ids };
  }

  const hits: Record<string, ProblemData> = {};
  const misses: string[] = [];
  const now = Date.now();
  const expiredIds: string[] = [];

  try {
    const database = getDb();
    const records = await database.problems.bulkGet(ids);

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const record = records[i];

      if (record) {
        // TTL 확인
        if (now - record.cachedAt > PROBLEM_TTL_MS) {
          expiredIds.push(id);
          misses.push(id);
        } else {
          try {
            const decrypted = await decrypt(record.encryptedData, record.iv);
            hits[id] = JSON.parse(decrypted);
          } catch (decryptError) {
            // 복호화 실패 (키 변경 등) - 캐시 미스로 처리
            console.warn(`[problemDb] 복호화 실패 (${id}):`, decryptError);
            expiredIds.push(id);
            misses.push(id);
          }
        }
      } else {
        misses.push(id);
      }
    }

    // 만료된 데이터 삭제
    if (expiredIds.length > 0) {
      await database.problems.bulkDelete(expiredIds);
    }
  } catch (error) {
    console.error("[problemDb] DB 조회 실패:", error);
    return { hits: {}, misses: ids };
  }

  return { hits, misses };
}

function isQuotaExceededError(error: unknown, seen = new Set<unknown>()): boolean {
  if (!error || typeof error !== "object" || seen.has(error)) return false;
  seen.add(error);

  const candidate = error as {
    name?: string;
    inner?: unknown;
    cause?: unknown;
    failures?: unknown[];
  };
  return (
    candidate.name === "QuotaExceededError" ||
    isQuotaExceededError(candidate.inner, seen) ||
    isQuotaExceededError(candidate.cause, seen) ||
    (Array.isArray(candidate.failures) &&
      candidate.failures.some((failure) => isQuotaExceededError(failure, seen)))
  );
}

/** 만료 캐시를 우선 정리하고, 재시도 전에 오래된 문제 캐시로 여유를 확보한다. */
async function evictProblemsForQuota(
  database: ProblemDatabase,
  incomingCount: number,
): Promise<number> {
  return database.transaction("rw", database.problems, async () => {
    const count = await database.problems.count();
    // 문제 크기는 서로 다르므로 건수는 정리 기준일 뿐 저장 성공을 보장하지 않는다.
    const targetCount = Math.max(incomingCount, Math.ceil(count * QUOTA_EVICTION_RATIO));
    const expiredCount = await database.problems
      .where("cachedAt")
      .below(Date.now() - PROBLEM_TTL_MS)
      .delete();

    if (expiredCount >= targetCount) return expiredCount;

    const oldestIds = await database.problems
      .orderBy("cachedAt")
      .limit(targetCount - expiredCount)
      .primaryKeys();
    await database.problems.bulkDelete(oldestIds);
    return expiredCount + oldestIds.length;
  });
}

async function writeProblemsToDb(
  problems: Record<string, ProblemData | null>,
  signal: AbortSignal,
): Promise<void> {
  const database = getDb();
  const now = Date.now();
  const records: EncryptedProblem[] = [];

  for (const [id, data] of Object.entries(problems)) {
    signal.throwIfAborted();
    if (data) {
      const { encryptedData, iv } = await encrypt(JSON.stringify(data));
      records.push({ id, encryptedData, iv, cachedAt: now });
    }
  }

  signal.throwIfAborted();
  if (records.length === 0) return;

  // 일부만 저장된 상태로 복구하지 않도록 최초 저장과 재시도를 각각 원자적으로 처리한다.
  const putRecords = () => database.transaction("rw", database.problems, () =>
    database.problems.bulkPut(records)
  );
  try {
    await putRecords();
  } catch (error) {
    signal.throwIfAborted();
    if (!isQuotaExceededError(error)) throw error;

    try {
      const removedCount = await evictProblemsForQuota(database, records.length);
      signal.throwIfAborted();
      console.warn(`[problemDb] 저장 공간 부족으로 문제 캐시 ${removedCount}개 정리 후 저장을 재시도합니다.`);
      await putRecords();
    } catch (retryError) {
      // 정리 또는 한 번의 재시도가 실패하면 새로고침 전까지 추가 저장을 중단한다.
      cacheWritesDisabled = true;
      throw retryError;
    }
  }
}

/** IndexedDB 저장 실패는 문제 조회를 막지 않으며, 공간 부족에 한해 한 번 복구한다. */
export async function saveProblemsToDb(
  problems: Record<string, ProblemData | null>
): Promise<void> {
  if (typeof window === "undefined" || cacheWritesDisabled) return;

  cacheWriteQueue = cacheWriteQueue.then(async () => {
    if (cacheWritesDisabled) return;
    try {
      await withTimeout(
        (signal) => {
          signal.addEventListener("abort", () => {
            cacheWritesDisabled = true;
          }, { once: true });
          return writeProblemsToDb(problems, signal);
        },
        CACHE_WRITE_TIMEOUT_MS,
        "문제 캐시 저장 시간이 초과되었습니다.",
      );
    } catch (error) {
      // 암호화 키를 localStorage에 보관하지 못한 경우에도 저장을 중단한다.
      if (isQuotaExceededError(error)) cacheWritesDisabled = true;
      console.error("[problemDb] DB 저장 실패:", error);
    }
  });
  return cacheWriteQueue;
}

/**
 * 단일 문제 데이터 조회
 */
export async function getProblemFromDb(
  id: string
): Promise<ProblemData | null> {
  const { hits } = await getProblemsFromDb([id]);
  return hits[id] || null;
}

/**
 * 단일 문제 데이터 저장
 */
export async function saveProblemToDb(
  id: string,
  data: ProblemData
): Promise<void> {
  await saveProblemsToDb({ [id]: data });
}

/**
 * 전체 문제 캐시 삭제
 */
export async function clearProblemsDb(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const database = getDb();
    await database.problems.clear();
    // 암호화 키도 함께 삭제하여 불일치 방지
    localStorage.removeItem(ENCRYPTION_KEY_NAME);
    cachedEncryptionKey = null;
    console.log("[problemDb] 캐시 전체 삭제 완료");
  } catch (error) {
    console.error("[problemDb] 캐시 삭제 실패:", error);
  }
}

/**
 * 만료된 캐시 정리
 */
export async function pruneExpiredProblems(): Promise<number> {
  if (typeof window === "undefined") return 0;

  try {
    const database = getDb();
    const expireTime = Date.now() - PROBLEM_TTL_MS;
    const expiredRecords = await database.problems
      .where("cachedAt")
      .below(expireTime)
      .toArray();

    if (expiredRecords.length > 0) {
      await database.problems.bulkDelete(expiredRecords.map((r) => r.id));
      console.log(`[problemDb] ${expiredRecords.length}개 만료 캐시 정리`);
    }

    return expiredRecords.length;
  } catch (error) {
    console.error("[problemDb] 만료 캐시 정리 실패:", error);
    return 0;
  }
}

/**
 * 캐시 통계 조회 (디버그용)
 */
export async function getDbStats(): Promise<{
  count: number;
  oldestCachedAt: Date | null;
  newestCachedAt: Date | null;
} | null> {
  if (typeof window === "undefined") return null;

  try {
    const database = getDb();
    const count = await database.problems.count();

    if (count === 0) {
      return { count: 0, oldestCachedAt: null, newestCachedAt: null };
    }

    const oldest = await database.problems.orderBy("cachedAt").first();
    const newest = await database.problems.orderBy("cachedAt").last();

    return {
      count,
      oldestCachedAt: oldest ? new Date(oldest.cachedAt) : null,
      newestCachedAt: newest ? new Date(newest.cachedAt) : null,
    };
  } catch (error) {
    console.error("[problemDb] 통계 조회 실패:", error);
    return null;
  }
}

// ============ 캐시 무효화 메타데이터 ============

/**
 * 마지막으로 확인한 서버 동기화 타임스탬프 조회
 */
export async function getLastSyncTimestamp(): Promise<number> {
  if (typeof window === "undefined") return 0;

  try {
    const database = getDb();
    const record = await database.meta.get("lastSyncTimestamp");
    return record?.value ?? 0;
  } catch (error) {
    console.error("[problemDb] 동기화 타임스탬프 조회 실패:", error);
    return 0;
  }
}

/**
 * 서버 동기화 타임스탬프 저장
 */
export async function setLastSyncTimestamp(ts: number): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    const database = getDb();
    await database.meta.put({ key: "lastSyncTimestamp", value: ts });
  } catch (error) {
    console.error("[problemDb] 동기화 타임스탬프 저장 실패:", error);
  }
}

/**
 * 특정 문제들의 캐시만 삭제 (캐시 무효화)
 */
export async function invalidateProblems(ids: string[]): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (ids.length === 0) return true;

  try {
    const database = getDb();
    await database.problems.bulkDelete(ids);
    console.log(`[problemDb] ${ids.length}개 문제 캐시 무효화 완료`);
    return true;
  } catch (error) {
    console.error("[problemDb] 캐시 무효화 실패:", error);
    return false;
  }
}
